import { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Loader2, ChevronLeft, Save, Plus, Trash2, CheckCircle2, Send, FileText, X, Upload,
} from "lucide-react";

export default function QuizEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [title, setTitle] = useState("");
  const [duration, setDuration] = useState(10);
  const [questions, setQuestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = () =>
    api.get(`/quizzes/${id}/edit`).then((r) => {
      setQuiz(r.data);
      setTitle(r.data.title);
      setDuration(r.data.duration_minutes);
      setQuestions(r.data.questions);
    }).catch((e) => { toast.error(errText(e)); navigate("/kuis"); });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const updateQ = (qi, patch) => setQuestions((qs) => qs.map((q, i) => (i === qi ? { ...q, ...patch } : q)));
  const updateOpt = (qi, oi, val) => updateQ(qi, { options: questions[qi].options.map((o, i) => (i === oi ? val : o)) });
  const addOpt = (qi) => updateQ(qi, { options: [...questions[qi].options, ""] });
  const removeOpt = (qi, oi) => {
    const q = questions[qi];
    if (q.options.length <= 2) return toast.error("Minimal 2 pilihan");
    const options = q.options.filter((_, i) => i !== oi);
    const ci = (q.correct_indices || []).filter((i) => i !== oi).map((i) => (i > oi ? i - 1 : i));
    updateQ(qi, { options, correct_indices: ci.length ? ci : [0] });
  };
  const toggleCorrect = (qi, oi) => {
    const cur = questions[qi].correct_indices || [];
    const next = cur.includes(oi) ? cur.filter((i) => i !== oi) : [...cur, oi].sort((a, b) => a - b);
    updateQ(qi, { correct_indices: next });
  };
  const addQuestion = () =>
    setQuestions((qs) => [...qs, { question: "", options: ["", ""], correct_indices: [0] }]);
  const removeQuestion = (qi) => setQuestions((qs) => qs.filter((_, i) => i !== qi));

  const save = async (silent = false) => {
    setSaving(true);
    try {
      await api.put(`/quizzes/${id}`, { title, duration_minutes: Number(duration), questions });
      if (!silent) toast.success("Perubahan soal disimpan");
      return true;
    } catch (e) {
      toast.error(errText(e));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const importQuestions = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const hasContent = questions.some((q) => q.question.trim() && q.options.filter((o) => o.trim()).length >= 2);
    if (hasContent) {
      const ok = await save(true);
      if (!ok) return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/quizzes/${id}/import-questions`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.imported} soal berhasil diimpor (total ${data.total} soal)`);
      await load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setImporting(false);
    }
  };

  const togglePublish = async () => {
    const ok = await save(true);
    if (!ok) return;
    setPublishing(true);
    try {
      const { data } = await api.post(`/quizzes/${id}/publish`);
      toast.success(data.published ? "Kuis dipublikasikan" : "Kuis ditarik ke draft");
      navigate("/kuis");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setPublishing(false);
    }
  };

  if (user && user.is_super !== true) return <Navigate to="/kuis" replace />;
  if (!quiz) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <button onClick={() => navigate("/kuis")} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2"><ChevronLeft className="h-4 w-4" /> Kembali ke Daftar Kuis</button>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">Bank Soal</h1>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${quiz.published ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
            {quiz.published ? "Dipublikasikan" : "Draft"}
          </span>
        </div>
        <p className="mt-1 flex items-center gap-1 text-sm text-slate-400"><FileText className="h-3.5 w-3.5" /> {quiz.source_filename}</p>
      </div>

      <div className="rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-800">Impor soal dari Excel / CSV</p>
            <p className="mt-0.5 text-xs text-slate-500">Kolom: <span className="font-medium">Soal</span>, <span className="font-medium">Pilihan A/B/C/D</span>, dan kolom <span className="font-medium">Benar A/B/C/D</span> berisi <span className="font-mono">TRUE/FALSE</span>. Soal ditambahkan ke kuis ini.</p>
          </div>
          <label data-testid="import-questions-button" className={`flex shrink-0 cursor-pointer items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 ${importing ? "opacity-60 pointer-events-none" : ""}`}>
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Impor Soal
            <input data-testid="import-questions-input" type="file" accept=".xlsx,.csv" className="hidden" onChange={importQuestions} />
          </label>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2">
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Judul Kuis</label>
          <input data-testid="edit-quiz-title" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Batas Waktu (menit)</label>
          <input data-testid="edit-quiz-duration" type="number" min="1" max="180" value={duration} onChange={(e) => setDuration(e.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
        </div>
      </div>

      <div className="space-y-4">
        {questions.map((q, qi) => (
          <div key={qi} data-testid={`edit-question-${qi}`} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="mt-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-sm font-bold text-orange-600">{qi + 1}</span>
              <textarea
                data-testid={`edit-question-text-${qi}`}
                value={q.question}
                onChange={(e) => updateQ(qi, { question: e.target.value })}
                rows={2}
                placeholder="Tulis pertanyaan..."
                className="flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium outline-none focus:border-blue-500 resize-none"
              />
              <button data-testid={`delete-question-${qi}`} onClick={() => removeQuestion(qi)} className="mt-2 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
            <p className="mt-3 ml-10 text-xs font-semibold text-slate-400">Klik lingkaran untuk menandai jawaban benar (boleh lebih dari satu)</p>
            <div className="mt-2 ml-10 space-y-2">
              {q.options.map((opt, oi) => (
                <div key={oi} className="flex items-center gap-2">
                  <button
                    data-testid={`mark-correct-${qi}-${oi}`}
                    onClick={() => toggleCorrect(qi, oi)}
                    title="Tandai benar"
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition ${(q.correct_indices || []).includes(oi) ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 text-transparent hover:border-emerald-400"}`}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                  </button>
                  <input
                    data-testid={`edit-option-${qi}-${oi}`}
                    value={opt}
                    onChange={(e) => updateOpt(qi, oi, e.target.value)}
                    placeholder={`Pilihan ${String.fromCharCode(65 + oi)}`}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm outline-none focus:border-blue-500 ${(q.correct_indices || []).includes(oi) ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200"}`}
                  />
                  <button onClick={() => removeOpt(qi, oi)} className="text-slate-300 hover:text-rose-500"><X className="h-4 w-4" /></button>
                </div>
              ))}
              <button data-testid={`add-option-${qi}`} onClick={() => addOpt(qi)} className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"><Plus className="h-3.5 w-3.5" /> Tambah Pilihan</button>
            </div>
          </div>
        ))}
        <button data-testid="add-question-button" onClick={addQuestion} className="w-full flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 py-4 text-sm font-semibold text-slate-500 hover:border-blue-400 hover:text-blue-600 transition">
          <Plus className="h-4 w-4" /> Tambah Soal ({questions.length} soal)
        </button>
      </div>

      <div className="sticky bottom-4 flex flex-wrap gap-3 rounded-2xl border border-slate-200 bg-white/90 backdrop-blur p-4 shadow-lg">
        <button data-testid="save-quiz-button" onClick={() => save()} disabled={saving} className="flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan Draft
        </button>
        <button data-testid="publish-quiz-button" onClick={togglePublish} disabled={publishing || saving} className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60 ${quiz.published ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
          {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {quiz.published ? "Tarik ke Draft" : "Simpan & Publikasikan"}
        </button>
      </div>
    </div>
  );
}
